// 以 HTTP 連到 Worker 的伺服器牌局
// 對應 server/api.ts 的 API 清單
//
// - 建局（/api/game/round）時，開局資訊（莊家、風位、底台）由伺服器的牌局組決定，瀏覽器不送
// - 動作類請求不自動重送：伺服器可能已經套用、只是回應沒收到，重送會被判成不合法
// - 這一手結束時，回應附帶伺服器結算結果；先寫回本機存檔快取，結算畫面照舊讀存檔
//
// === 使用圖片 ===
// 無 — 純連線

import type { TPlayerAction } from '@core/actions';
import type { TRoundEventMap } from '@core/events';
import type { TSeat } from '@core/types';
import type { IEventBus } from '@event-bus/event-bus';
import { apiPost, applySnapshot } from './account-client';
import { CRemoteRound, type IRoundTransport } from './remote-round';
import type { IWireBatch, IWireState } from './round-wire';
import type { IDealResult } from './table-session';

const httpTransport = (roundId: string): IRoundTransport => {
  const url = (op: string): string => `/api/rounds/${roundId}/${op}`;
  return {
    deal: () => apiPost<IDealResult>(url('deal')),
    begin: () => apiPost<IWireBatch>(url('begin')),
    act: (seat: TSeat, action: TPlayerAction) => apiPost<IWireBatch>(url('act'), { seat, action }),
    tenpai: (seat: TSeat) => apiPost<IWireBatch>(url('tenpai'), { seat }),
    advance: (seat: TSeat, proxy: boolean) => apiPost<IWireBatch>(url('advance'), { seat, proxy }),
  };
};

export interface ICreateHttpRoundInput {
  readonly playerSeat: TSeat;
  readonly bus: IEventBus<TRoundEventMap>;
  readonly onError: (error: unknown) => void;
}

/** 在伺服器為目前這一手建局並取回發牌結果；完成後 dealStaged 可同步呼叫。 */
export const createHttpRound = async (input: ICreateHttpRoundInput): Promise<CRemoteRound> => {
  const created = await apiPost<{ roundId: string; state: IWireState }>('/api/game/round');
  const round = new CRemoteRound(httpTransport(created.roundId), input.bus, {
    playerSeat: input.playerSeat,
    initialState: created.state,
    onError: input.onError,
    onSettlement: (settlement) => applySnapshot(settlement.snapshot),
  });
  await round.prepare();
  return round;
};
